import { Vector3 } from '@babylonjs/core';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';
import { ObstacleBouncePadBehavior } from './ObstacleBouncePadBehavior';
import { ObstacleCannonBehavior } from './ObstacleCannonBehavior';
import { ObstacleFireBehavior } from './ObstacleFireBehavior';
import { ObstacleGeyserBehavior } from './ObstacleGeyserBehavior';
import { ObstacleHammerBehavior } from './ObstacleHammerBehavior';
import { ObstacleHorizontalMoveBehavior } from './ObstacleHorizontalMoveBehavior';
import { ObstacleIdleBehavior } from './ObstacleIdleBehavior';
import { ObstacleJumpBehavior } from './ObstacleJumpBehavior';
import { ObstacleLaserGridBehavior } from './ObstacleLaserGridBehavior';
import { ObstacleMagnetBehavior } from './ObstacleMagnetBehavior';
import { ObstaclePatrolBehavior } from './ObstaclePatrolBehavior';
import { ObstaclePulseBehavior } from './ObstaclePulseBehavior';
import { ObstacleRocketBehavior } from './ObstacleRocketBehavior';
import { ObstacleRollBehavior } from './ObstacleRollBehavior';
import { ObstacleRotateBehavior } from './ObstacleRotateBehavior';
import { ObstacleScaleBehavior } from './ObstacleScaleBehavior';
import { ObstacleSeekerBeamBehavior } from './ObstacleSeekerBeamBehavior';
import { ObstacleSpikeRingBehavior } from './ObstacleSpikeRingBehavior';
import { ObstacleSwingBehavior } from './ObstacleSwingBehavior';
import { ObstacleTurretBehavior } from './ObstacleTurretBehavior';
import { ObstacleVerticalMoveBehavior } from './ObstacleVerticalMoveBehavior';
import { ObstacleWindZoneBehavior } from './ObstacleWindZoneBehavior';

export function createObstacleBehavior(
  config: ObstacleDistributionConfig,
  startPosition: Vector3,
  pathIndex: number,
  lateralOffset?: number
): ObstacleBehavior {
  config.speed = config.speed ?? 1;
  config.radius = config.radius ?? 5;
  config.height = config.height ?? 2;
  config.width = config.width ?? 5;
  config.angle = config.angle ?? Math.PI / 3;
  config.direction = config.direction ?? 1;
  config.axis = config.axis ?? 'y';
  config.rotation = config.rotation ?? new Vector3(0, 0, 0);
  config.yOffset = config.yOffset ?? 0;
  config.scaleRange = config.scaleRange ?? { min: 0.8, max: 1.2 };
  config.fireInterval = config.fireInterval ?? 2;
  config.fireActiveTime = config.fireActiveTime ?? 1;
  config.fireHeight = config.fireHeight ?? 5;
  config.direction = config.direction ?? 1;
  config.fireWidth = config.fireWidth ?? 1;
  config.fireEmitterGrow = config.fireEmitterGrow ?? false;

  switch (config.behavior) {
    case 'patrol':
      return new ObstaclePatrolBehavior(startPosition, pathIndex, config);
    case 'rotate':
      return new ObstacleRotateBehavior(startPosition, pathIndex, config);
    case 'jump':
      return new ObstacleJumpBehavior(startPosition, pathIndex, config);
    case 'horizontal':
      return new ObstacleHorizontalMoveBehavior(startPosition, pathIndex, config);
    case 'vertical':
      return new ObstacleVerticalMoveBehavior(startPosition, pathIndex, config);
    case 'scale':
      return new ObstacleScaleBehavior(startPosition, pathIndex, config);
    case 'pulse':
      return new ObstaclePulseBehavior(startPosition, pathIndex, config);
    case 'rocket':
      return new ObstacleRocketBehavior(startPosition, pathIndex, config, lateralOffset);
    case 'roll':
      return new ObstacleRollBehavior(startPosition, pathIndex, config);
    case 'fire':
      return new ObstacleFireBehavior(startPosition, pathIndex, config);
    case 'swing':
      return new ObstacleSwingBehavior(startPosition, pathIndex, config);
    case 'cannon':
      return new ObstacleCannonBehavior(startPosition, pathIndex, config);
    case 'hammer':
      return new ObstacleHammerBehavior(startPosition, pathIndex, config);
    case 'seekerBeam':
      return new ObstacleSeekerBeamBehavior(startPosition, pathIndex, config);
    case 'geyser':
      return new ObstacleGeyserBehavior(startPosition, pathIndex, config);
    case 'bouncePad':
      return new ObstacleBouncePadBehavior(startPosition, pathIndex, config);
    case 'magnet':
      return new ObstacleMagnetBehavior(startPosition, pathIndex, config);
    case 'spikeRing':
      return new ObstacleSpikeRingBehavior(startPosition, pathIndex, config);
    case 'laserGrid':
      return new ObstacleLaserGridBehavior(startPosition, pathIndex, config);
    case 'turret':
      return new ObstacleTurretBehavior(startPosition, pathIndex, config);
    case 'windZone':
      return new ObstacleWindZoneBehavior(startPosition, pathIndex, config);
    case 'idle':
    default:
      return new ObstacleIdleBehavior(startPosition, pathIndex, config);
  }
}
